import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Loader2, MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { OperatorConfigurationState } from '@/lib/operator-config/types'
import { trpc } from '@/lib/trpc'

export type AddressValue = {
  addressText: string
  lat?: string
  lng?: string
  h3Index?: string
}

export type PostalAddressValue = AddressValue & {
  line1: string
  line2: string
  city: string
  region: string
  postalCode: string
}

type Suggestion = {
  placeId: string
  displayName: string
  lat: number
  lng: number
}

function splitRegionPostal(value: string) {
  const trimmed = value.trim()
  const match = trimmed.match(/^([A-Za-z.\- ]+?)\s+(\d[\dA-Za-z -]*)$/)

  if (!match) {
    return { region: trimmed, postalCode: '' }
  }

  return {
    region: match[1]?.trim() ?? '',
    postalCode: match[2]?.trim() ?? '',
  }
}

export function parsePostalAddress(value: AddressValue): PostalAddressValue {
  const text = value.addressText.trim()
  const parts = text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

  const [line1 = text, city = '', regionPostal = ''] = parts
  const line2 =
    parts.length > 3
      ? parts.slice(1, parts.length - 2).join(', ')
      : parts.length === 4
        ? (parts[1] ?? '')
        : ''
  const cityValue = parts.length >= 3 ? (parts[parts.length - 2] ?? city) : city
  const regionPostalValue = parts.length >= 2 ? (parts[parts.length - 1] ?? regionPostal) : ''
  const { region, postalCode } = splitRegionPostal(regionPostalValue)

  return {
    line1,
    line2,
    city: cityValue,
    region,
    postalCode,
    addressText: text,
    lat: value.lat,
    lng: value.lng,
    h3Index: value.h3Index,
  }
}

function formatPostalAddress(value: PostalAddressValue) {
  return [
    value.line1,
    value.line2,
    value.city,
    [value.region, value.postalCode].filter(Boolean).join(' '),
  ]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ')
}

function parseSuggestionAddress(displayName: string) {
  const parts = parsePostalAddress({ addressText: displayName })
  return {
    line1: parts.line1,
    line2: parts.line2,
    city: parts.city,
    region: parts.region,
    postalCode: parts.postalCode,
  }
}

export function AddressField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string
  value: AddressValue
  placeholder: string
  onChange: (next: AddressValue) => void
}) {
  const [query, setQuery] = useState(value.addressText)
  const [picked, setPicked] = useState<Suggestion | null>(null)
  const [debouncedQuery, setDebouncedQuery] = useState(value.addressText)

  useEffect(() => {
    setQuery(value.addressText)
  }, [value.addressText])

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 250)
    return () => window.clearTimeout(timeout)
  }, [query])

  const autocompleteQuery = trpc.geo.autocomplete.useQuery(
    { query: debouncedQuery, limit: 5 },
    { enabled: debouncedQuery.trim().length >= 3 && !picked }
  )

  const geocodeQuery = trpc.geo.geocode.useQuery(
    { address: picked?.displayName ?? value.addressText },
    {
      enabled: Boolean(
        (picked?.displayName || value.addressText) && !(value.lat && value.lng && value.h3Index)
      ),
    }
  )

  useEffect(() => {
    if (!picked || !geocodeQuery.data) return

    onChange({
      addressText: picked.displayName,
      lat: String(geocodeQuery.data.lat),
      lng: String(geocodeQuery.data.lng),
      h3Index: geocodeQuery.data.h3Index,
    })
  }, [geocodeQuery.data, onChange, picked])

  const suggestions = (autocompleteQuery.data ?? []) as Suggestion[]

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        value={query}
        placeholder={placeholder}
        onChange={(event) => {
          const nextAddress = event.target.value
          setQuery(nextAddress)
          setPicked(null)
          onChange({
            addressText: nextAddress,
            lat: undefined,
            lng: undefined,
            h3Index: undefined,
          })
        }}
      />
      {!picked && suggestions.length > 0 && query.trim().length >= 3 ? (
        <div className="rounded-[6px] border border-border/70 bg-background/90 p-2 shadow-sm">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.placeId}
              type="button"
              className="flex w-full cursor-pointer items-start gap-2 rounded-[4px] px-3 py-2 text-left transition-colors hover:bg-muted"
              onClick={() => {
                setQuery(suggestion.displayName)
                setPicked(suggestion)
                onChange({
                  addressText: suggestion.displayName,
                  lat: String(suggestion.lat),
                  lng: String(suggestion.lng),
                  h3Index: undefined,
                })
              }}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm">{suggestion.displayName}</span>
            </button>
          ))}
        </div>
      ) : null}
      {geocodeQuery.isFetching ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking address
          </span>
        </div>
      ) : null}
    </div>
  )
}

export function PostalAddressField({
  value,
  onChange,
}: {
  value: PostalAddressValue
  onChange: (next: PostalAddressValue) => void
}) {
  const [picked, setPicked] = useState<Suggestion | null>(null)
  const [debouncedQuery, setDebouncedQuery] = useState(formatPostalAddress(value))
  const formattedAddress = formatPostalAddress(value)

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(formattedAddress), 250)
    return () => window.clearTimeout(timeout)
  }, [formattedAddress])

  const autocompleteQuery = trpc.geo.autocomplete.useQuery(
    { query: debouncedQuery, limit: 5 },
    { enabled: debouncedQuery.trim().length >= 5 && !picked }
  )

  const geocodeQuery = trpc.geo.geocode.useQuery(
    { address: picked?.displayName ?? formattedAddress },
    {
      enabled: Boolean(
        (picked?.displayName || formattedAddress) && !(value.lat && value.lng && value.h3Index)
      ),
    }
  )

  useEffect(() => {
    if (!picked || !geocodeQuery.data) return

    onChange({
      ...value,
      ...parseSuggestionAddress(picked.displayName),
      addressText: picked.displayName,
      lat: String(geocodeQuery.data.lat),
      lng: String(geocodeQuery.data.lng),
      h3Index: geocodeQuery.data.h3Index,
    })
  }, [geocodeQuery.data, onChange, picked, value])

  const suggestions = (autocompleteQuery.data ?? []) as Suggestion[]

  function patchAddress(next: Partial<PostalAddressValue>) {
    const updated = {
      ...value,
      ...next,
    }

    onChange({
      ...updated,
      addressText: formatPostalAddress(updated),
      lat: undefined,
      lng: undefined,
      h3Index: undefined,
    })
    setPicked(null)
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        <div className="space-y-2">
          <Label>Street address</Label>
          <Input
            value={value.line1}
            placeholder="123 Main St"
            onChange={(event) => patchAddress({ line1: event.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Address line 2</Label>
          <Input
            value={value.line2}
            placeholder="Suite, unit, building"
            onChange={(event) => patchAddress({ line2: event.target.value })}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-[1.2fr_0.9fr_0.8fr]">
          <div className="space-y-2">
            <Label>City</Label>
            <Input
              value={value.city}
              placeholder="San Francisco"
              onChange={(event) => patchAddress({ city: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>State / region</Label>
            <Input
              value={value.region}
              placeholder="CA"
              onChange={(event) => patchAddress({ region: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Postal code</Label>
            <Input
              value={value.postalCode}
              placeholder="94103"
              onChange={(event) => patchAddress({ postalCode: event.target.value })}
            />
          </div>
        </div>
      </div>

      {!picked && suggestions.length > 0 ? (
        <div className="rounded-[6px] border border-border/70 bg-background/90 p-2 shadow-sm">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.placeId}
              type="button"
              className="flex w-full cursor-pointer items-start gap-2 rounded-[4px] px-3 py-2 text-left transition-colors hover:bg-muted"
              onClick={() => {
                setPicked(suggestion)
                onChange({
                  ...value,
                  ...parseSuggestionAddress(suggestion.displayName),
                  addressText: suggestion.displayName,
                  lat: String(suggestion.lat),
                  lng: String(suggestion.lng),
                  h3Index: undefined,
                })
              }}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm">{suggestion.displayName}</span>
            </button>
          ))}
        </div>
      ) : null}

      {geocodeQuery.isFetching ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking address
          </span>
        </div>
      ) : null}
    </div>
  )
}

export function matchesServiceAreaPolicy(
  location: OperatorConfigurationState['locations'][number],
  area: OperatorConfigurationState['serviceArea']
) {
  if (!area?.centerLat || !area.centerLng || !location.lat || !location.lng) return false
  return area.centerLat === location.lat && area.centerLng === location.lng
}

export function matchesServiceArea(
  location: OperatorConfigurationState['locations'][number],
  policies: OperatorConfigurationState['serviceAreaPolicies']
) {
  return policies.some((policy) => matchesServiceAreaPolicy(location, policy))
}
